import { trackEvent } from "@/lib/analytics";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/** Monthly premium price, shown in the UI. Charged server-side. */
export const PREMIUM_PRICE_LABEL = "$13.50";
export const PREMIUM_PRICE_PERIOD = "per month";

export type PremiumStatus = "none" | "pending" | "active" | "past_due" | "cancelled" | "expired";

export interface PremiumState {
  premium: boolean;
  status: PremiumStatus;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

const IDLE: PremiumState = {
  premium: false,
  status: "none",
  current_period_end: null,
  cancel_at_period_end: false,
};

/** Refresh a near-expiry session so checkout never fails with an opaque 401. */
async function ensureValidSession() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Please sign in to manage Statlign Premium.");
  const expiresAt = (session.expires_at ?? 0) * 1000;
  if (expiresAt - Date.now() < 60_000) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      throw new Error("Your session has expired. Please sign in again.");
    }
  }
}

/**
 * Authoritative premium state for the signed-in member.
 *
 * The answer comes from the database (`my_premium_status`), which reads the
 * subscription written only by the Paystack webhook — so the client cannot
 * grant itself access by editing local state.
 */
export function usePremium() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["premium-status", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<PremiumState> => {
      const { data, error } = await supabase.rpc("my_premium_status");
      if (error) throw error;
      return { ...IDLE, ...((data ?? {}) as Partial<PremiumState>) };
    },
  });

  const state = user ? (query.data ?? IDLE) : IDLE;

  return {
    ...state,
    /** True only once we know the answer — avoids flashing locked content. */
    isLoading: !!user && query.isLoading,
    isSignedIn: !!user,
    refetch: query.refetch,
  };
}

/** Starts Paystack checkout for the $13.50/month plan and redirects to it. */
export function useStartPremiumCheckout() {
  return useMutation({
    mutationFn: async (opts?: { currency?: "USD" | "NGN" }) => {
      await ensureValidSession();
      trackEvent("begin_checkout", { product: "premium", currency: opts?.currency ?? "USD" });
      const { data, error } = await supabase.functions.invoke("paystack-subscribe", {
        body: { origin: window.location.origin, currency: opts?.currency ?? "USD" },
      });
      if (error) throw error;
      if (data?.already_active) return data as { already_active: true };
      if (!data?.checkout_url) throw new Error("Could not start checkout. Please try again.");
      window.location.href = data.checkout_url as string;
      return data as { checkout_url: string };
    },
  });
}

/** Confirms a checkout after Paystack redirects back to /premium. */
export function useVerifyPremiumCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reference: string) => {
      await ensureValidSession();
      const { data, error } = await supabase.functions.invoke("paystack-premium-verify", {
        body: { reference },
      });
      if (error) throw error;
      return data as { status: string; current_period_end?: string };
    },
    onError: () => trackEvent("payment_failed", { product: "premium", status: "verify_error" }),
    onSuccess: (res) => {
      if (res?.status === "active") trackEvent("subscribe", { product: "premium", value: 12, currency: "USD" });
      else if (res?.status && res.status !== "pending") trackEvent("payment_failed", { product: "premium", status: res.status });
      void queryClient.invalidateQueries({ queryKey: ["premium-status"] });
    },
  });
}

/** Cancels renewal (access runs to period end) or reactivates the plan. */
export function useManagePremium() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: "cancel" | "reactivate") => {
      await ensureValidSession();
      const { data, error } = await supabase.functions.invoke("paystack-premium-manage", {
        body: { action },
      });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error ?? "Please try again in a moment.");
      return data as { ok: boolean; action: string };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["premium-status"] });
    },
  });
}

/**
 * Ad-free + live alerts. Premium members always have them; any paid credit pack
 * unlocks them for 30 days after purchase (server-decided via my_member_perks).
 */
export function useMemberPerks() {
  const { user } = useAuth();
  const { premium, isLoading: premiumLoading } = usePremium();
  const query = useQuery({
    queryKey: ["member-perks", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_member_perks");
      if (error) throw error;
      return (data ?? {}) as { perks?: boolean; until?: string | null };
    },
  });
  return {
    perks: premium || !!query.data?.perks,
    until: query.data?.until ?? null,
    isLoading: premiumLoading || (!!user && query.isLoading),
  };
}
